import { createApp } from 'vue'
import axios from 'axios'
import { REGIONS, PREF_TO_REGION } from '~/js/templates/page/stores/constants'

const CLASS_NAME_STORE_LIST_INFO = 'js-curtain-store-list-info'
// Shopify APIのmetaobjectsクエリは最大250件まで取得可能
const METAOBJECTS_PAGE_SIZE = 250
// Shopify APIバージョン
const SHOPIFY_API_VERSION = '2025-01'

/**
 * Metaobjectのstoresタイプを全件取得する
 * @returns {Promise<Array>} 取得した店舗データの配列
 */
const fetchAllStoresWithAxios = async () => {
  const element = document.getElementById('store_list_info')
  if (!element) return []

  const dataset = element.dataset
  const storefrontAccessToken = dataset.storefrontAccessToken
  if (!storefrontAccessToken) return []

  let allStores = []
  let hasNextPage = true
  let cursor = null

  while (hasNextPage) {
    try {
      const response = await axios.post(
        `/api/${SHOPIFY_API_VERSION}/graphql.json`,
        {
          query: `
            query($type: String!, $cursor: String) {
              metaobjects(type: $type, first: ${METAOBJECTS_PAGE_SIZE}, after: $cursor) {
                nodes {
                  id
                  handle
                  fields {
                    key
                    value
                  }
                }
                pageInfo {
                  hasNextPage
                  endCursor
                }
              }
            }
          `,
          variables: {
            type: 'stores',
            cursor: cursor,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          },
        }
      )
      if (response.data.errors) {
        console.error('GraphQL Errors:', response.data.errors)
        break
      }

      const data = response.data.data.metaobjects
      // フィルタ: order_curtain_display が true かつ is_closed が true でないもののみ
      const filteredNodes = data.nodes.filter((node) => {
        const fieldMap = node.fields.reduce((acc, field) => {
          acc[field.key] = field.value
          return acc
        }, {})
        const isCurtainDisplayedValue = fieldMap['order_curtain_display']
        const isClosedValue = fieldMap['is_closed']
        const isCurtainDisplay =
          isCurtainDisplayedValue === true || isCurtainDisplayedValue === 'true'
        const isClosed = isClosedValue === true || isClosedValue === 'true'
        return isCurtainDisplay && !isClosed
      })
      allStores = allStores.concat(filteredNodes)
      hasNextPage = data.pageInfo.hasNextPage
      cursor = data.pageInfo.endCursor
    } catch (error) {
      console.error('Axios Error:', error.response?.data || error.message)
      break
    }
  }
  return allStores
}

/**
 * 店舗データをREGIONSごとにグループ化し、各グループ内で都道府県順に並び替え
 * @param {Array} allStores 取得した店舗データの配列
 * @returns {Object} グループ化された店舗データ
 */
const groupStoresByRegion = (allStores) => {
  // 地域ごとにグループ化
  const groups = {}
  allStores.forEach((store) => {
    const fieldMap = store.fields.reduce((acc, field) => {
      acc[field.key] = field.value
      return acc
    }, {})
    // キャッシュとしてstoreに追加
    store._fieldMap = fieldMap
    const pref = fieldMap['prefecture']
    if (!pref) return
    const region = PREF_TO_REGION[pref]
    if (!region) return
    if (!groups[region]) groups[region] = []
    groups[region].push(store)
  })
  // REGIONS順で並び替え、各グループ内は都道府県順
  const sortedGroups = {}
  Object.keys(REGIONS).forEach((region) => {
    if (groups[region]) {
      sortedGroups[region] = groups[region].slice().sort((a, b) => {
        const aPref = a._fieldMap?.prefecture || ''
        const bPref = b._fieldMap?.prefecture || ''
        return REGIONS[region].indexOf(aPref) - REGIONS[region].indexOf(bPref)
      })
    }
  })
  return sortedGroups
}

document.addEventListener('DOMContentLoaded', async () => {
  const [elementStoreListInfo] =
    document.getElementsByClassName(CLASS_NAME_STORE_LIST_INFO) || []
  if (!elementStoreListInfo) return

  try {
    const allStores = await fetchAllStoresWithAxios()
    const groupedStores = groupStoresByRegion(allStores)

    createApp({
      name: 'OrderCurtainStoreList',
      data() {
        return {
          groupedStores,
          iconRightSvgUrl: elementStoreListInfo.getAttribute(
            'data-icon-right-svg-url'
          ),
        }
      },
      methods: {
        getField(store, key) {
          // _fieldMapがあればそれを使う
          if (store._fieldMap) {
            return store._fieldMap[key] || ''
          }
          return store.fields.find((f) => f.key === key)?.value || ''
        },
      },
    }).mount(elementStoreListInfo)
  } catch (e) {
    console.error('Failed to fetch stores:', e)
  }
})
